'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Heart, AlertCircle } from 'lucide-react';
import { formatPrice } from '@/lib/utils/format';
import { useCart } from '@/providers/CartProvider';
import { useWishlist } from '@/providers/WishlistProvider';
import { useNotification } from '@/providers/NotificationProvider';
import Image from 'next/image';
import { CartItemPopulated } from '@/lib/Apis';
import { useProduct } from '@/hooks/useProducts';

interface OutOfStockCartItemProps {
    item: CartItemPopulated;
}

export function OutOfStockCartItem({ item }: OutOfStockCartItemProps) {
    const { removeFromCart } = useCart();
    const { addToWishlist } = useWishlist();
    const notification = useNotification();
    const [isRemoving, setIsRemoving] = useState(false);
    const [isMoving, setIsMoving] = useState(false);

    // Fallback logic for media
    const productId =
        (item as any).productId ||
        (item as any).product?._id ||
        (item as any).product?.id ||
        (item.productVariantData as any).productId ||
        (item.productVariantData as any).product?._id ||
        (item.productVariantData as any).product?.id;

    const { product } = useProduct(productId);

    const handleRemove = async () => {
        setIsRemoving(true);
        try {
            await removeFromCart(item.productVariantId);
        } finally {
            setIsRemoving(false);
        }
    };

    const handleMoveToWishlist = async () => {
        setIsMoving(true);
        try {
            // Note: We need a Product object for addToWishlist. 
            // We'll construct a partial one if needed, but the API might only need the ID.
            // Based on our research, it seems the API takes a productId. 
            // We'll try to find it in the data or fallback to variantId if they are the same in some contexts.
            const productId = (item.productVariantData as any).productId || item.productVariantId;

            await addToWishlist({
                _id: productId,
                name: item.productVariantData.name,
            } as any);

            await removeFromCart(item.productVariantId);

            notification.success(
                'Moved to wishlist',
                `${item.productVariantData.name} has been moved to your wishlist.`
            );
        } catch (error) {
            console.error('Error moving to wishlist:', error);
            // removeFromCart is already handled by error UI in provider if it fails
        } finally {
            setIsMoving(false);
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-gray-50/50 border-2 border-dashed border-gray-200 rounded-[24px] p-4 md:p-6 opacity-75 grayscale-[0.5]"
        >
            <div className="flex gap-4">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-[16px] overflow-hidden bg-gray-100 shrink-0 relative">
                    <Image
                        src={(item as any).product?.media?.[0]?.file || product?.media?.[0]?.file || item.productVariantData.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                        alt={(item as any).product?.name || product?.name || item.productVariantData.name}
                        className="w-full h-full object-cover opacity-50"
                        height={96}
                        width={96}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/80 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-bold text-red-600 border border-red-200">
                            OUT OF STOCK
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-500 line-through">
                                {(item as any).product?.name || product?.name || item.productVariantData.name}
                            </h3>
                            <p className="text-[12px] text-gray-400 mb-1">{item.productVariantData.name}</p>
                            <div className="flex items-center gap-2 text-red-500 mb-3">
                                <AlertCircle className="size-3" />
                                <span className="text-[11px] font-medium">Currently unavailable</span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={handleMoveToWishlist}
                                disabled={isMoving || isRemoving}
                                className="p-2 rounded-full bg-white border border-gray-200 hover:border-hprimary hover:text-hprimary transition-all group"
                                title="Move to wishlist"
                            >
                                {isMoving ? (
                                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-hprimary border-t-transparent" />
                                ) : (
                                    <Heart className="size-4 text-gray-400 group-hover:text-hprimary transition-colors" />
                                )}
                            </button>
                            <button
                                onClick={handleRemove}
                                disabled={isRemoving || isMoving}
                                className="p-2 rounded-full bg-white border border-gray-200 hover:border-red-200 hover:text-red-500 transition-all group"
                                title="Remove item"
                            >
                                {isRemoving ? (
                                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                                ) : (
                                    <Trash2 className="size-4 text-gray-400 group-hover:text-red-500 transition-colors" />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-400">
                            {formatPrice(item.productVariantData.finalPrice)}
                        </p>
                        <span className="text-[11px] text-gray-400 italic">
                            Saved in cart but out of stock
                        </span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
